/**
 * Performance utility: Debounce function to limit rapid successive calls
 * Useful for optimizing socket event handlers and state updates
 */

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Throttle function to limit function calls to at most once per interval
 * Better for continuous events like scroll or resize
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Batch updates to prevent excessive re-renders
 * Collects multiple updates and applies them all at once
 */
export const batchUpdates = (updateFunction, delay = 50) => {
  let updates = [];
  let timeoutId;

  return (update) => {
    updates.push(update);

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // Defensive: updates is always non-empty here (push precedes setTimeout),
      // but we guard against hypothetical race conditions.
      /* istanbul ignore next */
      if (updates.length > 0) {
        updateFunction(updates);
        updates = [];
      }
    }, delay);
  };
};
